import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { DEFAULT_SPACE_NAME, registerRequestSchema } from "@vmeste/shared";
import { AuthShell } from "../components/AuthShell";
import { offerEncryptionAfterRegister } from "./WelcomePage";
import { Button, PasswordField, TextField } from "../components/ui";
import { useSession } from "../app/session";
import { validate } from "../lib/form";
import { useT } from "../i18n/index.js";

/**
 * Registration is invite-only (SPEC §4.1): there is no open form and no way to
 * reach this screen without a code, so the code field comes first rather than
 * last. A `?code=` in the link pre-fills it.
 *
 * This screen does **not** look the code up before it is submitted, and that is
 * why it says nothing about who invited whom or into what: everything it could
 * claim would be a guess. The one place that matters is the name of the space —
 * an app code creates one for this person, and only they can name it — so the
 * invite link written in Settings carries `kind=app` alongside the code, and the
 * field below appears for it.
 *
 * The hint is a hint and nothing more. What the code actually does is settled by
 * the code itself on the server, which reads `spaceName` only when the code
 * names no space; a link with the marker filed off simply falls back to the
 * default name, and a marker on a code into somebody's space changes nothing
 * about where that person lands.
 */
export function RegisterPage() {
  const { session, register, pending, error } = useSession();
  const t = useT();
  const location = useLocation();
  const [params] = useSearchParams();

  const [inviteCode, setInviteCode] = useState(() => params.get("code") ?? "");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [spaceName, setSpaceName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const ownSpace = params.get("kind") === "app";

  // Back to whatever they were opening, if anything sent them here.
  const from = (location.state as { from?: { pathname: string; search: string } } | null)
    ?.from;

  /**
   * A space of one's own, and nowhere else to be first.
   *
   * The welcome screen offers encryption while the space is still empty, which
   * is the only moment turning it on costs nothing. It is worth exactly one
   * detour and not a redirect: somebody who arrived here from a shared link is
   * in the middle of doing something, and the encryption card in Settings is
   * still there for them.
   */
  const offering = ownSpace && !from;
  const destination = from
    ? `${from.pathname}${from.search}`
    : offering
      ? "/welcome"
      : "/lists";

  if (session) return <Navigate to={destination} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Built here rather than at module scope: a map made once on import would
    // keep the language it was loaded in after the person switches.
    const named = spaceName.trim();
    const result = validate(
      registerRequestSchema,
      {
        inviteCode,
        displayName,
        email,
        password,
        // Absent rather than empty when nobody typed anything: the schema wants
        // at least one character, and "" would fail the form over a field the
        // person was told they could leave alone.
        ...(ownSpace && named ? { spaceName: named } : {}),
      },
      {
        inviteCode: t("auth.inviteCodeRequired"),
        displayName: t("auth.nameRequired"),
        email: t("auth.emailInvalid"),
        password: t("auth.passwordTooShort"),
        spaceName: t("auth.spaceNameInvalid"),
      },
    );
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    /*
     * A note for the next screen, left before the request rather than after it:
     * the redirect below fires the moment the session lands, and a line waiting
     * on `register` could lose the race with it. A note left by a registration
     * that then failed harms nothing — /welcome is unreachable without one, and
     * a successful sign-up in this tab is the only thing that goes there.
     */
    if (offering) offerEncryptionAfterRegister();
    // The whole parsed request goes through, `spaceName` and all: the session's
    // `register` takes the object as it is and hands it to the API client, which
    // sends the body it was given.
    await register(result.value);
  }

  return (
    <AuthShell
      title={t("auth.registerTitle")}
      lede={t("auth.registerLede")}
      footer={
        <>
          {t("auth.haveAccount")}{" "}
          <Link to="/signin" className="text-accent underline underline-offset-4">
            {t("auth.signIn")}
          </Link>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <TextField
          label={t("auth.inviteCode")}
          name="inviteCode"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          placeholder="VM-0000-0000"
          className="font-mono tracking-wide"
          value={inviteCode}
          error={errors["inviteCode"]}
          onChange={(e) => setInviteCode(e.target.value)}
        />
        {ownSpace ? (
          <TextField
            label={t("auth.spaceName")}
            name="spaceName"
            maxLength={120}
            hint={t("auth.spaceNameHint", { name: DEFAULT_SPACE_NAME })}
            value={spaceName}
            error={errors["spaceName"]}
            onChange={(e) => setSpaceName(e.target.value)}
          />
        ) : null}
        <TextField
          label={t("auth.name")}
          name="displayName"
          autoComplete="nickname"
          hint={t("auth.nameHint")}
          value={displayName}
          error={errors["displayName"]}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <TextField
          label={t("auth.email")}
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          value={email}
          error={errors["email"]}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label={t("auth.password")}
          showLabel={t("auth.showPassword")}
          hideLabel={t("auth.hidePassword")}
          name="password"
          autoComplete="new-password"
          hint={t("auth.passwordHint")}
          value={password}
          error={errors["password"]}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error ? (
          <p role="alert" className="rounded-card bg-neg-soft px-3 py-2 text-[0.875rem] text-neg">
            {error}
          </p>
        ) : null}

        <Button type="submit" full disabled={pending}>
          {pending ? t("auth.registering") : t("auth.acceptInvite")}
        </Button>
      </form>
    </AuthShell>
  );
}
